import clsx from "clsx";
import { graphql } from "gatsby";
import React, { DetailedHTMLProps, FC, Fragment, HTMLAttributes } from "react";

import { getResolvedThemeColors } from "../../../styles/resolved-config";
import { CommonColors } from "../../../types/colors";
import { Cta } from "../../buttons/cta";
import { ContentfulImage } from "../../contentful-image";
import { RichText } from "../../rich-text";
import { descriptionStyles } from "./styles";
import { MaterialCardColorScheme, MaterialCardDescriptionColors } from "./types";

export interface MaterialCardProps
  extends Queries.MaterialCardDataFragment,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  keepAspectRatio?: boolean;
  showBody?: boolean;
}

export const MaterialCard: FC<MaterialCardProps> = ({
  variant: _variant,
  description,
  descriptionColor: _descriptionColor,
  image,
  secondaryImage,
  logo,
  secondaryLogo,
  ctas,
  className,
  keepAspectRatio = true,
  showBody = true,
}) => {
  const variant = _variant as MaterialCardColorScheme;
  const themeColors = getResolvedThemeColors();

  const materialBgColor = themeColors[variant];
  const descriptionColor = (_descriptionColor ??
    CommonColors.Dark) as MaterialCardDescriptionColors;
  const descriptionColorOnHover =
    variant === MaterialCardColorScheme.Primary ? CommonColors.Light : CommonColors.Dark;

  const descriptionClassName =
    descriptionStyles[descriptionColor] ?? descriptionStyles[CommonColors.Dark];

  const descriptionClassNameOnHover =
    descriptionStyles[descriptionColorOnHover] ?? descriptionStyles[CommonColors.Dark];

  const ctaContent = (
    <div
      className={clsx(
        "max-h-0 overflow-hidden opacity-0 transition-all duration-700",
        "group-hover:opacity-100 group-hover:max-h-40 group-hover:md:max-h-44"
      )}
    >
      {(ctas ?? []).length > 0 && (
        <div className="flex flex-col gap-3 h-auto pt-9">
          {ctas?.filter(Boolean).map((cta, index) => (
            <Fragment key={index}>
              <Cta variant="light" size="sm" className="xl:hidden" {...cta!} fullWidth />
              <Cta variant="light" className="hidden xl:block" {...cta!} fullWidth />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={clsx(
        "relative rounded-xl overflow-hidden group",
        keepAspectRatio && "aspect-[3/4]",
        className
      )}
    >
      <div
        className={clsx(
          "absolute top-0 left-0 z-20 w-full transition-opacity",
          !showBody && "opacity-0",
          descriptionColor === CommonColors.Light && "bg-dark bg-opacity-40 h-full"
        )}
      >
        {logo && (
          <ContentfulImage
            image={logo}
            gatsbyImageProps={{ objectFit: "contain" }}
            className="m-4 max-h-24 [&_img]:!w-fit [&_img]:max-w-[90%]"
          />
        )}
        {description?.raw && (
          <RichText
            raw={description.raw}
            className={clsx("font-light p-4 text-xl line-clamp-[7]", descriptionClassName)}
          />
        )}
      </div>

      <div className="absolute z-20 bottom-0 px-8 w-full h-full">
        {/* Bottom overlay layer: background and secondary image */}
        <span
          className={clsx(
            "absolute bottom-0 left-0",
            "transition-all duration-500",
            "group-hover:h-full w-full bg-primary h-3 -z-10",
            !showBody && "opacity-0"
          )}
          style={{
            backgroundColor: materialBgColor,
          }}
        >
          <div className="relative w-full h-full">
            {secondaryImage && (
              <div
                className={clsx(
                  "absolute invisible group-hover:visible bottom-10 w-full h-[50%]",
                  "duration-1000 transition-[right] right-[-50%] group-hover:right-[-33%] "
                )}
              >
                <ContentfulImage
                  image={secondaryImage}
                  gatsbyImageProps={{ objectFit: "contain" }}
                  alt=""
                />
              </div>
            )}
          </div>
        </span>
        {/* secondary overlay */}
        <span
          className={clsx(
            "absolute left-0 group-hover:top-0 top-[calc(100%_-_8px)] h-full pl-4 py-2 pr-1",
            "flex flex-col justify-between transition-all duration-500 w-full",
            !showBody && "opacity-0"
          )}
        >
          <div
            className={clsx(
              "overflow-auto pr-3 pb-2",
              descriptionColor === CommonColors.Light && "light-scrollbar",
              descriptionColor === CommonColors.Primary && "primary-scrollbar",
              descriptionColor === CommonColors.Dark && "neutral-scrollbar"
            )}
          >
            <div>
              {secondaryLogo && (
                <ContentfulImage
                  image={secondaryLogo}
                  gatsbyImageProps={{ objectFit: "contain" }}
                  className="mr-8 mb-6 max-h-24 [&_img]:!w-fit [&_img]:max-w-[90%]"
                />
              )}
              {description?.raw && (
                <RichText
                  raw={description.raw}
                  className={clsx("font-light text-xl line-clamp-[7]", descriptionClassNameOnHover)}
                />
              )}
            </div>
            {ctaContent}
          </div>
        </span>
      </div>

      {/* main background image */}
      {image && <ContentfulImage image={image} alt="" className="w-full h-full" />}
    </div>
  );
};

export const query = graphql`
  fragment MaterialCardData on ContentfulMaterialCard {
    __typename
    variant
    description {
      raw
    }
    descriptionColor
    image {
      ...ContentfulImageData
    }
    logo {
      ...ContentfulImageData
    }
    secondaryLogo {
      ...ContentfulImageData
    }
    secondaryImage {
      ...ContentfulImageData
    }
    ctas {
      ...CtaData
    }
  }
`;
